/* CART CONTRACT — do not rename (bundle-discount Function):
   _bundle_id (UUID), _bundle_role: "main" | "addon"
   PREVIEW: Math.round(cents * (100 - percent) / 100) */

const DEBOUNCE_MS = 100;
const previewCents = (c, p) => Math.round((c * (100 - p)) / 100);

const SAR_ICON =
  '<svg class="sar-currency-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 11 13" aria-hidden="true" focusable="false"><path fill="currentColor" d="M10.21 7.76a3.815 3.815 0 0 1-.321 1.194l-3.554.75v-2.25l-1.107.234v1.249a.54.54 0 0 1-.095.307l-.576.849a.998.998 0 0 1-.618.41L.8 11.166a3.85 3.85 0 0 1 .321-1.193l3-.634V7.922l-2.799.592c.043-.422.154-.823.322-1.193l2.477-.524V2.422A3.899 3.899 0 0 1 5.228 1.5v5.064l1.107-.234V2.973a3.9 3.9 0 0 1 1.107-.924v4.046l2.768-.584a3.81 3.81 0 0 1-.321 1.193l-2.447.517v1.125l2.768-.585Zm-3.875 4.194c.043-.42.154-.822.322-1.193l3.553-.75a3.814 3.814 0 0 1-.321 1.192l-3.554.751Z"></path></svg>';

function fmtMoney(cents, format, currency) {
  const cur = window.theme?.Currency;
  const f = format || window.money_format || cur?.moneyFormat;
  if (cur?.formatProductMoneyHtml) return cur.formatProductMoneyHtml(cents, f);
  const v = (cents / 100).toFixed(2);
  const inner = (f || "${{amount}}").replace(/\{\{\s*\w+\s*\}\}/, v);
  const curCode = currency || window.shop_currency;
  if (curCode === "SAR") {
    return `<span class="price-with-sar price-with-sar--sar-active" dir="ltr" translate="no"><span class="price-with-sar__glyph" aria-hidden="true">${SAR_ICON}</span><span class="money" data-currency="SAR">${inner}</span></span>`;
  }
  return inner;
}

function productUrl(handle) {
  const base = window.router || "";
  return `${base}/products/${encodeURIComponent(handle)}.js`;
}

function normalizeOptions(product) {
  if (!product?.options?.length) return [];
  const first = product.options[0];
  if (typeof first === "string") {
    return product.options.map((name, index) => ({
      name,
      index,
      values: [...new Set(product.variants.map((v) => v.options[index]).filter(Boolean))],
    }));
  }
  return product.options.map((opt, i) => {
    const index = (opt.position ?? i + 1) - 1;
    const values =
      opt.values?.length > 0
        ? opt.values
        : [...new Set(product.variants.map((v) => v.options[index]).filter(Boolean))];
    return { name: opt.name || String(opt), index, values };
  });
}

function hasVariantOptions(product) {
  if (!product?.variants?.length) return false;
  if (product.variants.length === 1) {
    const v = product.variants[0];
    return v.title !== "Default Title" && v.title !== product.title;
  }
  const opts = normalizeOptions(product);
  if (!opts.length) return product.variants.length > 1;
  return opts.some((o) => o.name !== "Title");
}

function findVariant(product, opts) {
  return product.variants.find((v) =>
    v.options.every((o, i) => opts[i] == null || opts[i] === o),
  );
}

function optAvailable(product, idx, val, opts, inventoryMap) {
  return product.variants.some((v) => {
    if (v.options[idx] !== val) return false;
    if (!variantPurchasable(v, inventoryMap)) return false;
    return v.options.every((o, i) => i === idx || opts[i] == null || opts[i] === o);
  });
}

function variantPurchasable(variant, inventoryMap) {
  if (!variant) return false;
  const meta = getVariantInventoryMeta(variant, inventoryMap);
  const available = meta?.available ?? variant.available;
  if (available) return true;
  return (meta?.inventory_policy ?? variant.inventory_policy) === "continue";
}

function parseInventoryMap(card) {
  try {
    const el = card?.querySelector("[data-offer-inventory]");
    return el ? JSON.parse(el.textContent) : {};
  } catch {
    return {};
  }
}

function getVariantInventoryMeta(variant, inventoryMap) {
  if (!variant) return null;
  const fromMap = inventoryMap?.[String(variant.id)];
  if (fromMap) {
    return {
      quantity: fromMap.quantity,
      inventory_management: fromMap.inventory_management,
      inventory_policy: fromMap.inventory_policy ?? variant.inventory_policy,
      available: fromMap.available ?? variant.available,
    };
  }
  return {
    quantity: variant.inventory_quantity,
    inventory_management: variant.inventory_management,
    inventory_policy: variant.inventory_policy,
    available: variant.available,
  };
}

function getInventoryQty(variant, inventoryMap) {
  const meta = getVariantInventoryMeta(variant, inventoryMap);
  if (!meta) return null;
  if (meta.quantity != null && !Number.isNaN(Number(meta.quantity))) {
    return parseInt(meta.quantity, 10);
  }
  if (meta.inventory_management == null) return null;
  return null;
}

/* Keep in sync with theme.js detectAvailabilityState + getInventoryQty */
function detectAvailabilityState(variant, inventoryMap) {
  if (!variant) return "unknown";
  const meta = getVariantInventoryMeta(variant, inventoryMap);
  const available = meta.available ?? variant.available;
  const policy = meta.inventory_policy ?? variant.inventory_policy;
  if (!available) {
    return policy === "continue" ? "preorder" : "outofstock";
  }
  const mgmt = meta.inventory_management ?? variant.inventory_management;
  if (mgmt == null) return "instock";
  const stockQty = getInventoryQty(variant, inventoryMap);
  if (stockQty === null) return "instock";
  if (stockQty > 0) return "instock";
  return "preorder";
}

function hasFullSelection(entry) {
  if (!entry?.product) return false;
  if (!hasVariantOptions(entry.product)) return true;
  return entry.selectedOptions.every((o) => o != null);
}

function isColorOption(name) {
  return /color|colour|لون/i.test(String(name));
}

function formatVariantLabel(variant, product) {
  if (!variant) return "";
  const title = variant.public_title || variant.title || "";
  if (!title || title === "Default Title") return "";
  if (product?.title && title === product.title) return "";
  return title;
}

function variantImageForOption(product, idx, val) {
  const v = product.variants.find((x) => x.options[idx] === val && x.featured_image?.src);
  if (v?.featured_image?.src) return v.featured_image.src;
  const withImg = product.variants.find((x) => x.options[idx] === val && x.image);
  return withImg?.image || withImg?.featured_image?.src || null;
}

function isConfigured(entry) {
  if (!entry?.product) return false;
  const { product, selectedOptions, selectedVariant, inventoryMap } = entry;
  if (!hasVariantOptions(product)) {
    return !!(variantPurchasable(selectedVariant, inventoryMap) && entry.selectedVariantId);
  }
  return (
    selectedOptions.every((o) => o != null) &&
    !!(variantPurchasable(selectedVariant, inventoryMap) && entry.selectedVariantId)
  );
}

function findFirstPurchasableVariant(product, inventoryMap) {
  return product.variants?.find((v) => variantPurchasable(v, inventoryMap)) || null;
}

function applyVariantToEntry(entry, variant) {
  if (!entry?.product || !variant) return;
  const opts = normalizeOptions(entry.product);
  const selectedOptions = opts.map(() => null);
  opts.forEach((opt) => {
    selectedOptions[opt.index] = variant.options[opt.index] ?? null;
  });
  entry.selectedOptions = selectedOptions;
  entry.selectedVariant = variant;
  entry.selectedVariantId = variantPurchasable(variant, entry.inventoryMap) ? variant.id : null;
}

function resolveBundleLocale() {
  const htmlLang = (document.documentElement.lang || "").toLowerCase();
  const shopifyLocale = (window.Shopify?.locale || "").toLowerCase();
  const path = document.location.pathname || "";
  if (
    htmlLang.startsWith("ar") ||
    shopifyLocale.startsWith("ar") ||
    path.startsWith("/ar") ||
    path.startsWith("/ar/")
  ) {
    return "ar";
  }
  return "en";
}

function readBundleStorefrontCopy(root) {
  try {
    const inline = root.querySelector("[data-bundle-storefront-copy]")?.textContent;
    if (inline) {
      const parsed = JSON.parse(inline);
      if (parsed && typeof parsed === "object") return parsed;
    }
  } catch (_) {
    /* ignore malformed inline JSON */
  }
  if (window.BB_BUNDLE_COPY && typeof window.BB_BUNDLE_COPY === "object") {
    return window.BB_BUNDLE_COPY;
  }
  return null;
}

function pickBundleCopyText(copy, locale) {
  if (!copy) return { heading: "", subheading: "" };
  const isAr = locale === "ar" || locale.startsWith("ar-");
  return {
    heading: String(isAr ? copy.heading_ar : copy.heading_en || "").trim(),
    subheading: String(isAr ? copy.subheading_ar : copy.subheading_en || "").trim(),
  };
}

const BUNDLE_DISCOUNT_TOKEN = "[%]";

function formatMaxDiscountPercent(pct) {
  const n = Math.round(Number(pct) || 0);
  return n > 0 ? `${n}%` : "";
}

function maxDiscountFromOffers(offers) {
  if (!Array.isArray(offers) || !offers.length) return 0;
  let max = 0;
  for (const offer of offers) {
    const pct = Math.round(Number(offer?.discountPercent) || 0);
    if (pct > max) max = pct;
  }
  return max;
}

function readBundleOffers(root) {
  try {
    const raw = root.querySelector("[data-bundle-offers]")?.textContent;
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

function applyDiscountToken(text, maxPct) {
  const value = String(text ?? "").trim();
  if (!value || !value.includes(BUNDLE_DISCOUNT_TOKEN)) return value;
  const replacement = formatMaxDiscountPercent(maxPct);
  return value.split(BUNDLE_DISCOUNT_TOKEN).join(replacement).replace(/\s{2,}/g, " ").trim();
}

class BundleBuilder extends HTMLElement {
  connectedCallback() {
    this.moneyFormat = this.dataset.moneyFormat || window.money_format || "${{amount}}";
    this.currency = this.dataset.currency || window.shop_currency || "";
    this.i18n = JSON.parse(this.querySelector("[data-bundle-i18n]")?.textContent || "{}");
    this.mainVariants = JSON.parse(this.querySelector("[data-bundle-main-variants]")?.textContent || "[]");
    this.state = {
      mainVariantId: +this.dataset.mainVariantId,
      mainVariantTitle: formatVariantLabel(
        { title: this.dataset.mainVariantTitle },
        { title: this.dataset.mainTitle },
      ),
      mainPriceCents: +this.dataset.mainPriceCents || 0,
      mainTitle: this.dataset.mainTitle || "",
      companions: new Map(),
    };
    this.productCache = new Map();
    this.debounceTimer = null;
    this.cacheDom();
    this.bindEvents();
    this._onDocPointerDown = this.onDocumentPointerDown.bind(this);
    document.addEventListener("mousedown", this._onDocPointerDown);
    this._onLanguageChanged = () => this.applyStorefrontCopy();
    window.addEventListener("languageChanged", this._onLanguageChanged);
    this.applyStorefrontCopy();
    this.renderSummary();
    this.initFirstOffer();
  }

  disconnectedCallback() {
    document.removeEventListener("mousedown", this._onDocPointerDown);
    if (this._onLanguageChanged) {
      window.removeEventListener("languageChanged", this._onLanguageChanged);
    }
  }

  applyStorefrontCopy() {
    const copy = readBundleStorefrontCopy(this);
    if (!copy) return;

    const maxPct = maxDiscountFromOffers(readBundleOffers(this));
    const raw = pickBundleCopyText(copy, resolveBundleLocale());
    const heading = applyDiscountToken(raw.heading, maxPct);
    const subheading = applyDiscountToken(raw.subheading, maxPct);
    const inner = this.querySelector(".bb-bundle-builder__inner");
    if (!inner) return;

    let header = inner.querySelector(".bb-bundle-builder__header");
    if (!heading && !subheading) {
      header?.remove();
      return;
    }

    if (!header) {
      header = document.createElement("header");
      header.className = "bb-bundle-builder__header";
      const layout = inner.querySelector(".bb-bundle-builder__layout");
      if (layout) {
        inner.insertBefore(header, layout);
      } else {
        inner.prepend(header);
      }
    } else {
      header.hidden = false;
    }

    let headingEl = header.querySelector(".bb-bundle-builder__heading");
    let subheadingEl = header.querySelector(".bb-bundle-builder__subheading");

    if (heading) {
      if (!headingEl) {
        headingEl = document.createElement("h2");
        headingEl.className = "bb-bundle-builder__heading";
        header.insertBefore(headingEl, header.firstChild);
      }
      headingEl.hidden = false;
      headingEl.textContent = heading;
    } else if (headingEl) {
      headingEl.remove();
    }

    if (subheading) {
      if (!subheadingEl) {
        subheadingEl = document.createElement("p");
        subheadingEl.className = "bb-bundle-builder__subheading";
        header.appendChild(subheadingEl);
      }
      subheadingEl.hidden = false;
      subheadingEl.textContent = subheading;
    } else if (subheadingEl) {
      subheadingEl.remove();
    }
  }

  money(cents) {
    return fmtMoney(cents, this.moneyFormat, this.currency);
  }

  polishPrices() {
    const cur = window.theme?.Currency;
    if (!cur?.polishSarPriceDisplay || !window.jQuery) return;
    cur.polishSarPriceDisplay(window.jQuery(this));
  }

  offerKey(card) {
    return card?.dataset.offerHandle || card?.dataset.offerId || "";
  }

  findCard(offerKey) {
    if (!offerKey) return null;
    return (
      this.querySelector(`[data-offer-handle="${CSS.escape(offerKey)}"]`) ||
      this.querySelector(`[data-offer-id="${CSS.escape(offerKey)}"]`)
    );
  }

  cacheDom() {
    this.summary = this.querySelector("[data-bundle-summary]");
    this.summaryAddons = this.querySelector("[data-summary-addons]");
    this.stickyCta = this.querySelector("[data-bundle-sticky-cta]");
    this.stickySave = this.querySelector("[data-sticky-save]");
    this.addButtons = this.querySelectorAll("[data-bundle-add], [data-bundle-add-sticky]");
    this.addButtonTexts = this.querySelectorAll(
      ".bb-bundle-summary__btn-text, .bb-bundle-sticky-cta__btn-text",
    );
    this.errorEl = this.querySelector("[data-bundle-error]");
    this.refreshOfferCards();
  }

  refreshOfferCards() {
    this.offerCards = this.querySelectorAll("[data-bundle-card][data-offer-handle]");
  }

  getCheckedCards() {
    return [...this.offerCards].filter((card) => card.querySelector(".bb-bundle-card__check")?.checked);
  }

  getSelectionState() {
    const checked = this.getCheckedCards();
    const items = checked.map((card) => {
      const key = this.offerKey(card);
      const entry = this.state.companions.get(key);
      const pct = +card.dataset.discountPercent || 0;
      return { card, key, entry, pct, configured: isConfigured(entry) };
    });
    const canAdd =
      items.length > 0 && items.every((item) => item.configured && item.entry?.selectedVariantId);
    return { checked, items, canAdd };
  }

  bindEvents() {
    this.addEventListener("change", (e) => {
      if (e.target.matches(".bb-bundle-card__check")) this.onCheckChange(e.target);
    });
    this.addEventListener("click", (e) => {
      const edit = e.target.closest("[data-offer-edit]");
      if (edit) {
        e.preventDefault();
        e.stopPropagation();
        const card = edit.closest("[data-bundle-card]");
        if (card) this.onEditOffer(card);
      }
    });
    this.addButtons.forEach((b) => b.addEventListener("click", () => this.addBundleToCart()));
    const root = document.querySelector('[data-section-type="product"]');
    if (!root) return;
    const sid = root.getAttribute("data-section-id") || root.id.replace("ProductSection-", "");
    if (window.jQuery) {
      const $r = window.jQuery(root);
      const onVar = (e) => e.variant && this.onMainVariantChange(e.variant);
      $r.on(`variantChange.slideshow-${sid}.bundleBuilder variantPriceChange.slideshow-${sid}.bundleBuilder`, onVar);
    }
    root.querySelectorAll(`#ProductSelect-${sid}, [name="id"], [data-layout4-form-variant-id]`).forEach((inp) => {
      inp.addEventListener("change", () => {
        const v = this.mainVariants.find((x) => x.id === +inp.value);
        if (v) this.onMainVariantChange(v);
      });
    });
    document.addEventListener("boostShowroom:nav", () => {
      const inp = root.querySelector('[name="id"], [data-layout4-form-variant-id]');
      if (inp) {
        const v = this.mainVariants.find((x) => x.id === +inp.value);
        if (v) this.onMainVariantChange(v);
      }
    });
  }

  onMainVariantChange(variant) {
    this.state.mainVariantId = variant.id;
    this.state.mainVariantTitle = formatVariantLabel(variant, { title: this.state.mainTitle });
    this.state.mainPriceCents = variant.price;
    this.renderSummary();
  }

  async initFirstOffer() {
    if (this._autoInitDone) return;
    this._autoInitDone = true;

    const card = [...this.offerCards].find((c) => !c.classList.contains("bb-bundle-card--disabled"));
    if (!card) return;

    const checkbox = card.querySelector(".bb-bundle-card__check");
    if (!checkbox || checkbox.checked || checkbox.disabled) return;

    checkbox.checked = true;
    await this.selectOfferCard(card, checkbox);
  }

  async selectOfferCard(card, checkbox) {
    const offerKey = this.offerKey(card);
    if (!offerKey) return;

    card.classList.add("bb-bundle-card--selected");
    checkbox.setAttribute("aria-expanded", "true");
    const entry = this.state.companions.get(offerKey);
    if (entry) entry.collapsed = false;
    card.classList.remove("bb-bundle-card--configured");
    const col = card.querySelector("[data-offer-collapsed]");
    if (col) col.hidden = true;
    const exp = card.querySelector("[data-offer-expand]");
    if (exp) {
      exp.hidden = false;
      exp.removeAttribute("aria-hidden");
      requestAnimationFrame(() => card.classList.add("bb-bundle-card--expanded"));
    }
    await this.loadCompanion(offerKey, card);
    this.renderSummary();
  }

  deselectCard(card) {
    card.classList.remove("bb-bundle-card--selected", "bb-bundle-card--configured", "bb-bundle-card--expanded");
    const exp = card.querySelector("[data-offer-expand]");
    const col = card.querySelector("[data-offer-collapsed]");
    if (exp) {
      exp.hidden = true;
      exp.setAttribute("aria-hidden", "true");
    }
    if (col) col.hidden = true;
    const key = this.offerKey(card);
    const entry = this.state.companions.get(key);
    if (entry) {
      entry.collapsed = false;
    }
    const cb = card.querySelector(".bb-bundle-card__check");
    if (cb) cb.setAttribute("aria-expanded", "false");
  }

  collapseCard(card, entry) {
    if (!isConfigured(entry)) return;
    entry.collapsed = true;
    card.classList.add("bb-bundle-card--configured");
    card.classList.remove("bb-bundle-card--expanded");
    const expand = card.querySelector("[data-offer-expand]");
    const collapsed = card.querySelector("[data-offer-collapsed]");
    if (expand) {
      expand.hidden = true;
      expand.setAttribute("aria-hidden", "true");
    }
    if (collapsed) {
      collapsed.hidden = false;
      this.updateCollapsedRow(card, entry);
    }
  }

  expandCard(card, entry) {
    if (entry) entry.collapsed = false;
    card.classList.remove("bb-bundle-card--configured");
    const collapsed = card.querySelector("[data-offer-collapsed]");
    const expand = card.querySelector("[data-offer-expand]");
    if (collapsed) collapsed.hidden = true;
    const checkbox = card.querySelector(".bb-bundle-card__check");
    if (checkbox?.checked && expand) {
      expand.hidden = false;
      expand.removeAttribute("aria-hidden");
      requestAnimationFrame(() => card.classList.add("bb-bundle-card--expanded"));
      checkbox.setAttribute("aria-expanded", "true");
    }
  }

  onDocumentPointerDown(e) {
    this.offerCards.forEach((card) => {
      const checkbox = card.querySelector(".bb-bundle-card__check");
      if (!checkbox?.checked || card.contains(e.target)) return;
      const key = this.offerKey(card);
      const entry = this.state.companions.get(key);
      if (entry && isConfigured(entry) && !entry.collapsed) {
        this.collapseCard(card, entry);
      }
    });
  }

  async onCheckChange(checkbox) {
    if (checkbox.disabled) return;
    const card = checkbox.closest("[data-bundle-card]");
    const offerKey = this.offerKey(card);
    if (!offerKey) return;

    if (checkbox.checked) {
      await this.selectOfferCard(card, checkbox);
    } else {
      this.deselectCard(card);
    }
    this.renderSummary();
  }

  onEditOffer(card) {
    const offerKey = this.offerKey(card);
    const entry = this.state.companions.get(offerKey);
    const checkbox = card.querySelector(".bb-bundle-card__check");
    if (checkbox && !checkbox.checked) {
      checkbox.checked = true;
      card.classList.add("bb-bundle-card--selected");
    }
    this.expandCard(card, entry);
    const firstOpt = card.querySelector(".bb-bundle-options__btn, .bb-bundle-options__swatch");
    firstOpt?.focus();
    this.renderSummary();
  }

  updateCollapsedRow(card, entry) {
    const v = entry.selectedVariant;
    if (!v) return;
    const pct = +card.dataset.discountPercent || 0;
    const prev = previewCents(v.price, pct);
    const variantEl = card.querySelector("[data-offer-collapsed-variant]");
    const priceEl = card.querySelector("[data-offer-collapsed-price]");
    const compareEl = card.querySelector("[data-offer-collapsed-compare]");
    const imgEl = card.querySelector("[data-offer-collapsed-image]");
    if (variantEl) {
      const label = formatVariantLabel(v, entry.product);
      variantEl.textContent = label;
      variantEl.hidden = !label;
    }
    if (compareEl) compareEl.innerHTML = this.money(v.price);
    if (priceEl) priceEl.innerHTML = this.money(prev);
    const img = v.featured_image?.src || v.image;
    if (img && imgEl) imgEl.src = img;
  }

  async loadCompanion(offerKey, card) {
    const inventoryMap = parseInventoryMap(card);
    let entry = this.state.companions.get(offerKey);
    if (entry?.product) {
      entry.inventoryMap = inventoryMap;
      this.buildOptions(offerKey, card, entry);
      return;
    }
    const handle = card.dataset.offerHandle || offerKey;
    if (!handle) return;

    const box = card.querySelector("[data-offer-options]");
    if (box) {
      box.innerHTML = `<p class="bb-bundle-options__loading">${this.i18n.select_options || "Loading…"}</p>`;
    }

    let product = this.productCache.get(handle);
    if (!product) {
      try {
        const res = await fetch(productUrl(handle));
        if (!res.ok) throw new Error("fetch failed");
        product = await res.json();
        this.productCache.set(handle, product);
      } catch {
        if (box) {
          box.innerHTML = `<p class="bb-bundle-options__error">${this.i18n.unavailable || "Unavailable"}</p>`;
        }
        return;
      }
    }

    const opts = normalizeOptions(product);
    const selectedOptions = opts.length ? opts.map(() => null) : product.options?.map(() => null) || [];

    if (!hasVariantOptions(product)) {
      const v = product.variants.find((x) => variantPurchasable(x, inventoryMap)) || product.variants[0];
      entry = {
        product,
        selectedOptions,
        selectedVariant: v,
        selectedVariantId: variantPurchasable(v, inventoryMap) ? v.id : null,
        inventoryMap,
        collapsed: false,
      };
    } else {
      entry = {
        product,
        selectedOptions,
        selectedVariant: null,
        selectedVariantId: null,
        inventoryMap,
        collapsed: false,
      };
      const defaultVariant = findFirstPurchasableVariant(product, inventoryMap);
      if (defaultVariant) applyVariantToEntry(entry, defaultVariant);
    }
    this.state.companions.set(offerKey, entry);
    this.buildOptions(offerKey, card, entry);
    this.renderSummary();
  }

  buildOptions(offerKey, card, entry) {
    const box = card.querySelector("[data-offer-options]");
    if (!box) return;
    const { product, selectedOptions } = entry;
    box.innerHTML = "";

    if (!hasVariantOptions(product)) {
      const v = product.variants.find((x) => variantPurchasable(x, entry.inventoryMap)) || product.variants[0];
      if (v) {
        entry.selectedVariant = v;
        entry.selectedVariantId = variantPurchasable(v, entry.inventoryMap) ? v.id : null;
      }
      this.updateCardPrices(card, entry);
      if (isConfigured(entry)) {
        this.collapseCard(card, entry);
      }
      return;
    }

    const opts = normalizeOptions(product);
    opts.forEach((opt) => {
      if (opt.name === "Title" && product.variants.length <= 1) return;
      const idx = opt.index;
      const fs = document.createElement("fieldset");
      fs.className = "bb-bundle-options__group";
      const lg = document.createElement("legend");
      lg.className = "bb-bundle-options__legend";
      lg.textContent = isColorOption(opt.name)
        ? this.i18n.option_color || opt.name
        : /size|مقاس/i.test(opt.name)
          ? this.i18n.option_size || opt.name
          : opt.name;
      fs.appendChild(lg);
      const wrap = document.createElement("div");
      wrap.className = "bb-bundle-options__values";
      const colorOpt = isColorOption(opt.name);
      opt.values.forEach((val) => {
        const ok = optAvailable(product, idx, val, selectedOptions, entry.inventoryMap);
        const btn = document.createElement("button");
        btn.type = "button";
        const pressed = selectedOptions[idx] === val;
        if (colorOpt) {
          btn.className = "bb-bundle-options__swatch";
          btn.setAttribute("aria-label", val);
          const imgSrc = variantImageForOption(product, idx, val);
          if (imgSrc) btn.style.backgroundImage = `url(${imgSrc})`;
        } else {
          btn.className = "bb-bundle-options__btn";
          btn.textContent = val;
        }
        btn.setAttribute("aria-pressed", pressed ? "true" : "false");
        btn.disabled = !ok;
        if (!ok) btn.setAttribute("aria-disabled", "true");
        btn.addEventListener("click", () => {
          clearTimeout(this.debounceTimer);
          this.debounceTimer = setTimeout(() => {
            entry.selectedOptions[idx] = val;
            const v = findVariant(product, entry.selectedOptions);
            entry.selectedVariant = v;
            entry.selectedVariantId = variantPurchasable(v, entry.inventoryMap) ? v.id : null;
            this.buildOptions(offerKey, card, entry);
            this.updateCardPrices(card, entry);
            this.renderSummary();
          }, DEBOUNCE_MS);
        });
        wrap.appendChild(btn);
      });
      fs.appendChild(wrap);
      box.appendChild(fs);
    });

    const v = findVariant(product, selectedOptions);
    if (v && selectedOptions.every((o) => o != null)) {
      entry.selectedVariant = v;
      entry.selectedVariantId = variantPurchasable(v, entry.inventoryMap) ? v.id : null;
    }
    this.updateCardPrices(card, entry);
  }

  formatStockLabel(text) {
    return String(text || "").replace(/2[–-]3/g, (m) => `<bdi dir="ltr">${m}</bdi>`);
  }

  setStockLabel(el, htmlOrText, { html = false } = {}) {
    const span = document.createElement("span");
    span.className = "bb-bundle-card__stock-text";
    if (html) span.innerHTML = htmlOrText;
    else span.textContent = htmlOrText;
    el.replaceChildren(span);
  }

  updateStockStatus(card, entry) {
    const el = card.querySelector("[data-offer-stock]");
    if (!el) return;
    if (card.classList.contains("bb-bundle-card--disabled")) {
      el.hidden = false;
      el.className = "bb-bundle-card__stock bb-bundle-card__stock--out";
      this.setStockLabel(el, this.i18n.unavailable || "Currently unavailable");
      return;
    }
    if (!hasFullSelection(entry)) {
      el.hidden = true;
      return;
    }
    const v = entry.selectedVariant;
    if (!v) {
      el.hidden = true;
      return;
    }
    el.hidden = false;
    el.classList.remove(
      "bb-bundle-card__stock--in",
      "bb-bundle-card__stock--out",
      "bb-bundle-card__stock--preorder",
    );
    const state = detectAvailabilityState(v, entry.inventoryMap);
    if (state === "instock") {
      el.classList.add("bb-bundle-card__stock--in");
      this.setStockLabel(el, this.i18n.in_stock || "In stock");
    } else if (state === "preorder") {
      el.classList.add("bb-bundle-card__stock--preorder");
      this.setStockLabel(
        el,
        this.formatStockLabel(
          this.i18n.pre_order_ships || "Pre-order · Ships in approx. 2–3 weeks",
        ),
        { html: true },
      );
    } else {
      el.classList.add("bb-bundle-card__stock--out");
      this.setStockLabel(el, this.i18n.out_of_stock || "Out of stock");
    }
  }

  updateCardPrices(card, entry) {
    const pct = +card.dataset.discountPercent || 0;
    const v = entry.selectedVariant || entry.product?.variants?.[0];
    if (!v) return;
    const prev = previewCents(v.price, pct);
    const original = v.price;
    const cmp = card.querySelector("[data-offer-compare]");
    const pr = card.querySelector("[data-offer-preview]");
    if (cmp) cmp.innerHTML = this.money(original);
    if (pr) pr.innerHTML = this.money(prev);
    const imgEl = card.querySelector("[data-offer-image]");
    const img = v.featured_image?.src || v.image;
    if (img && imgEl) {
      imgEl.src = img;
      imgEl.srcset = img;
    }
    this.updateStockStatus(card, entry);
    this.polishPrices();
  }

  formatSaveBanner(amountCents, percent) {
    const tpl = this.i18n.save_banner || "You save {{ amount }} ({{ percent }}%)";
    return tpl
      .replace(/\{\{\s*amount\s*\}\}/, this.money(amountCents))
      .replace(/\{\{\s*percent\s*\}\}/, String(percent));
  }

  renderAddonSummaryLines(items) {
    if (!this.summaryAddons) return;
    this.summaryAddons.innerHTML = "";
    items.forEach(({ entry, pct, configured }) => {
      const line = document.createElement("div");
      line.className = "bb-bundle-summary__line";
      const detail = document.createElement("div");
      detail.className = "bb-bundle-summary__detail";
      const name = document.createElement("span");
      name.className = "bb-bundle-summary__name";
      name.textContent = entry?.product?.title || "";
      const variant = document.createElement("span");
      variant.className = "bb-bundle-summary__variant";
      if (configured && entry?.selectedVariant) {
        const label = formatVariantLabel(entry.selectedVariant, entry.product);
        variant.textContent = label;
        variant.hidden = !label;
      } else {
        variant.textContent = this.i18n.select_options || "Select options";
        variant.hidden = false;
      }
      detail.append(name, variant);
      line.appendChild(detail);

      const prices = document.createElement("div");
      prices.className = "bb-bundle-summary__addon-prices";
      if (configured && entry?.selectedVariant) {
        const v = entry.selectedVariant;
        const prev = previewCents(v.price, pct);
        const compare = document.createElement("s");
        compare.className = "bb-bundle-summary__compare";
        compare.innerHTML = this.money(v.price);
        const sale = document.createElement("span");
        sale.className = "bb-bundle-summary__price bb-bundle-summary__price--sale";
        sale.innerHTML = this.money(prev);
        prices.append(compare, sale);
      }
      line.appendChild(prices);
      this.summaryAddons.appendChild(line);
    });
  }

  renderSummary() {
    const { items, canAdd } = this.getSelectionState();
    const show = items.length > 0;
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    if (this.summary) this.summary.hidden = !show;
    if (this.stickyCta) this.stickyCta.hidden = !show || isMobile;
    if (!show) {
      this.addButtons.forEach((b) => (b.disabled = true));
      if (this.summaryAddons) this.summaryAddons.innerHTML = "";
      return;
    }

    this.querySelector("[data-summary-main-title]").textContent = this.state.mainTitle;
    const mainVariantEl = this.querySelector("[data-summary-main-variant]");
    if (mainVariantEl) {
      mainVariantEl.textContent = this.state.mainVariantTitle;
      mainVariantEl.hidden = !this.state.mainVariantTitle;
    }
    this.querySelector("[data-summary-main-price]").innerHTML = this.money(this.state.mainPriceCents);

    this.renderAddonSummaryLines(items);

    let save = 0;
    let total = this.state.mainPriceCents;
    let compareTotal = this.state.mainPriceCents;
    const offerDiscountPercents = [];

    items.forEach(({ entry, pct, configured }) => {
      if (!configured || !entry?.selectedVariant) return;
      const v = entry.selectedVariant;
      const prev = previewCents(v.price, pct);
      save += v.price - prev;
      total += prev;
      compareTotal += v.price;
      offerDiscountPercents.push(pct);
    });

    const saveBanner = this.querySelector("[data-summary-save-banner]");
    const savePercent =
      offerDiscountPercents.length > 0
        ? Math.round(
            offerDiscountPercents.reduce((sum, p) => sum + p, 0) / offerDiscountPercents.length,
          )
        : 0;
    if (saveBanner) {
      if (canAdd && save > 0) {
        saveBanner.hidden = false;
        saveBanner.innerHTML = this.formatSaveBanner(save, savePercent);
      } else {
        saveBanner.hidden = true;
        saveBanner.textContent = "";
      }
    }

    const totalCompareEl = this.querySelector("[data-summary-total-compare]");
    if (totalCompareEl) {
      if (canAdd && save > 0) {
        totalCompareEl.hidden = false;
        totalCompareEl.innerHTML = this.money(compareTotal);
      } else {
        totalCompareEl.hidden = true;
        totalCompareEl.textContent = "";
      }
    }

    this.querySelector("[data-summary-total]").innerHTML = this.money(
      canAdd ? total : compareTotal,
    );
    this.addButtons.forEach((b) => (b.disabled = !canAdd));

    if (this.stickySave) {
      if (canAdd && save > 0) {
        this.stickySave.hidden = false;
        this.stickySave.innerHTML = this.formatSaveBanner(save, savePercent);
      } else {
        this.stickySave.hidden = true;
        this.stickySave.textContent = "";
      }
    }

    if (this.errorEl) {
      this.errorEl.hidden = true;
      this.errorEl.textContent = "";
    }

    this.polishPrices();
  }

  buildCartPopupItem(cartLine) {
    const variant = this.mainVariants.find((v) => v.id === this.state.mainVariantId);
    let variantOptions = cartLine?.variant_options;
    if (!variantOptions?.length && variant?.options?.length) {
      variantOptions = variant.options.filter(Boolean);
    }
    if (!variantOptions?.length && this.state.mainVariantTitle) {
      variantOptions = [this.state.mainVariantTitle];
    }
    if (!variantOptions?.length) {
      variantOptions = ["Default Title"];
    }

    const properties = cartLine?.properties ? { ...cartLine.properties } : null;

    return {
      product_title: cartLine?.product_title || this.state.mainTitle,
      title: cartLine?.title || cartLine?.product_title || this.state.mainTitle,
      image:
        cartLine?.image ||
        variant?.featured_image?.src ||
        variant?.image ||
        null,
      variant_options: variantOptions,
      properties,
    };
  }

  async addBundleToCart() {
    const { canAdd, items } = this.getSelectionState();
    if (!canAdd || !this.state.mainVariantId) return;
    const addLabel = this.i18n.add_bundle || "";
    this.addButtons.forEach((b) => {
      b.disabled = true;
      b.classList.add("is-loading");
    });
    this.addButtonTexts.forEach((el) => (el.textContent = this.i18n.adding || ""));
    const uuid = crypto.randomUUID();
    const base = window.router || "";
    const cartItems = [
      {
        id: this.state.mainVariantId,
        quantity: 1,
        properties: { _bundle_id: uuid, _bundle_role: "main" },
      },
      ...items.map(({ entry, pct }) => ({
        id: entry.selectedVariantId,
        quantity: 1,
        properties: {
          _bundle_id: uuid,
          _bundle_role: "addon",
          _bundle_discount_percent: String(pct),
        },
      })),
    ];
    try {
      const res = await fetch(`${base}/cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: cartItems }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.description);
      this.addButtonTexts.forEach((el) => (el.textContent = this.i18n.added || ""));
      document.dispatchEvent(new CustomEvent("cart:refresh"));
      const addedItems = data.items ?? (Array.isArray(data) ? data : [data]);
      const mainLine = addedItems[0];
      if (mainLine) {
        window.theme?.CartPopup?.show?.(this.buildCartPopupItem(mainLine), { quantity: 1 });
      }
      setTimeout(() => {
        this.addButtons.forEach((b) => b.classList.remove("is-loading"));
        this.addButtonTexts.forEach((el) => (el.textContent = addLabel));
        this.renderSummary();
      }, 2000);
    } catch {
      if (this.errorEl) {
        this.errorEl.hidden = false;
        this.errorEl.textContent = this.i18n.add_error || "";
      }
      this.addButtons.forEach((b) => b.classList.remove("is-loading"));
      this.addButtonTexts.forEach((el) => (el.textContent = addLabel));
      this.renderSummary();
    }
  }
}

if (!customElements.get("bundle-builder")) customElements.define("bundle-builder", BundleBuilder);
